
import { useState } from "react";
import { PlatformStates } from "../types";
import { testApiConnection } from "../ApiKeyValidator";
import { toast } from "sonner";

export const useConnectionTester = (
  platformStates: PlatformStates,
  setPlatformStates: React.Dispatch<React.SetStateAction<PlatformStates>>
) => {
  const [testingConnections, setTestingConnections] = useState<Record<string, boolean>>({});

  const handleTestConnection = async (platformId: string) => {
    setTestingConnections(prev => ({ ...prev, [platformId]: true }));
    
    // Update state to show validating status
    setPlatformStates(prev => ({
      ...prev,
      [platformId]: {
        ...prev[platformId],
        status: 'validating',
        message: 'Testing connection...'
      }
    }));

    try {
      const platformState = platformStates[platformId];
      if (!platformState || !platformState.credentials) {
        throw new Error('No credentials found to test');
      }

      // Filter out masked credentials for testing
      const credentialsToTest = Object.fromEntries(
        Object.entries(platformState.credentials).filter(
          ([_, value]) => value && value !== '••••••••••••••••'
        )
      );

      if (Object.keys(credentialsToTest).length === 0) {
        throw new Error('No valid credentials available for testing');
      }

      console.log('Testing connection for platform:', platformId);
      const validationResult = await testApiConnection(platformId, credentialsToTest);
      
      setPlatformStates(prev => ({
        ...prev,
        [platformId]: {
          ...prev[platformId],
          status: validationResult.isValid ? 'valid' : 'invalid',
          message: validationResult.message
        }
      }));

      if (validationResult.isValid) {
        toast.success(`${platformId} connection test successful!`);
      } else {
        toast.error(`${platformId} connection test failed: ${validationResult.message}`);
      }
      
    } catch (error) {
      console.error('Error testing connection:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      
      setPlatformStates(prev => ({
        ...prev,
        [platformId]: {
          ...prev[platformId],
          status: 'error',
          message: `Connection test failed: ${errorMessage}`
        }
      }));
      
      toast.error(`Connection test failed: ${errorMessage}`);
    } finally {
      setTestingConnections(prev => ({ ...prev, [platformId]: false }));
    }
  };

  return {
    testingConnections,
    handleTestConnection
  };
};
